import { useCallback, useEffect, useRef, useState } from 'react';
import Vapi from '@vapi-ai/web';
import type { StartInterviewCall } from '../services/api';

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error';

export type MicrophonePermission = 'granted' | 'prompt' | 'denied' | 'unsupported';

export interface TranscriptEntry {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  transcriptType: 'partial' | 'final';
  timestamp: string;
  raw: unknown;
}

export interface VapiEvent {
  id: string;
  type: string;
  timestamp: string;
  payload?: unknown;
}

interface RawVapiMessage {
  type?: string;
  role?: 'user' | 'assistant' | 'system';
  transcript?: string;
  text?: string;
  message?: string;
  transcriptType?: 'partial' | 'final';
  isFinal?: boolean;
  final?: boolean;
  timestamp?: string | number;
  [key: string]: unknown;
}

interface VapiInstanceLike {
  on: (event: string, callback: (payload?: unknown) => void) => void;
  off?: (event: string, callback: (payload?: unknown) => void) => void;
  removeListener?: (event: string, callback: (payload?: unknown) => void) => void;
  start: (assistantId: string) => Promise<unknown>;
  reconnect?: (call: Record<string, unknown>) => Promise<unknown>;
  stop: () => void;
  isMuted?: () => boolean;
  setMuted?: (muted: boolean) => void;
}

export interface StartCallInput {
  assistantId: string;
  call?: StartInterviewCall;
}

export interface UseVapiReturn {
  status: ConnectionStatus;
  transcript: TranscriptEntry[];
  events: VapiEvent[];
  error: Error | null;
  micPermission: MicrophonePermission;
  requestMicrophoneAccess: () => Promise<MicrophonePermission>;
  startCall: (input: StartCallInput) => Promise<void>;
  endCall: () => void;
  isMuted: boolean;
  setMuted: (muted: boolean) => void;
}

const MAX_EVENTS = 300;
const MAX_TRANSCRIPT_ENTRIES = 500;
const CONNECT_TIMEOUT_MS = 20000;

function createId(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
}

function asString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as Record<string, unknown>;
}

function extractErrorParts(error: unknown): {
  message?: string;
  type?: string;
  stage?: string;
} {
  if (error instanceof Error) {
    return { message: error.message };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  const record = asRecord(error);
  if (!record) {
    return {};
  }

  const directMessage = asString(record.message);
  const type = asString(record.type);
  const stage = asString(record.stage);

  const nestedError = record.error;
  if (nestedError instanceof Error) {
    return {
      message: directMessage ?? nestedError.message,
      type,
      stage,
    };
  }

  const nestedErrorRecord = asRecord(nestedError);
  const nestedMessage = nestedErrorRecord ? asString(nestedErrorRecord.message) : undefined;
  const flatErrorMessage = asString(nestedError);
  const fallbackMessage = directMessage ?? nestedMessage ?? flatErrorMessage;

  if (fallbackMessage) {
    return { message: fallbackMessage, type, stage };
  }

  if (type && stage) {
    return { message: `VAPI ${type} (${stage})`, type, stage };
  }

  if (type) {
    return { message: `VAPI ${type}`, type, stage };
  }

  return { type, stage };
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  const parsed = extractErrorParts(error);
  if (parsed.message) {
    return new Error(parsed.message);
  }

  return new Error('Unknown VAPI error');
}

function toTranscriptEntry(message: unknown): TranscriptEntry | null {
  if (!message || typeof message !== 'object') {
    return null;
  }

  const payload = message as RawVapiMessage;

  // Accept transcript messages OR conversation-update messages with transcript content
  // VAPI sends different message types: 'transcript', 'conversation-update', etc.
  const isTranscriptType = payload.type === 'transcript';
  const isConversationUpdate = payload.type === 'conversation-update';

  // Extract text from various possible fields
  const text = payload.transcript ?? payload.text ?? payload.message;

  // For conversation-update, check if there's transcript content in the payload
  const conversationContent = payload.content as string | undefined;

  const finalText = text ?? conversationContent;

  // Must have either transcript type OR have text content with a valid role
  if (!isTranscriptType && !isConversationUpdate) {
    // Check if this is a message with transcript-like content (has role + text)
    const hasTranscriptContent =
      typeof finalText === 'string' &&
      finalText.trim() !== '' &&
      (payload.role === 'user' || payload.role === 'assistant');

    if (!hasTranscriptContent) {
      return null;
    }
  }

  if (typeof finalText !== 'string' || finalText.trim() === '') {
    return null;
  }

  const parsedTimestamp =
    payload.timestamp !== undefined ? new Date(payload.timestamp) : new Date();

  const derivedTranscriptType =
    payload.transcriptType ??
    (payload.isFinal === true || payload.final === true ? 'final' : undefined);

  return {
    id: createId('transcript'),
    role: payload.role ?? 'system',
    text: finalText.trim(),
    transcriptType: derivedTranscriptType ?? 'final',
    timestamp: Number.isNaN(parsedTimestamp.getTime())
      ? new Date().toISOString()
      : parsedTimestamp.toISOString(),
    raw: payload,
  };
}

function normalizeTranscriptText(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

function isCloseInTime(previousTimestamp: string, nextTimestamp: string, maxMs: number): boolean {
  const previousTime = new Date(previousTimestamp).getTime();
  const nextTime = new Date(nextTimestamp).getTime();

  if (Number.isNaN(previousTime) || Number.isNaN(nextTime)) {
    return false;
  }

  return Math.abs(nextTime - previousTime) <= maxMs;
}

function isLikelyIncrementalTranscript(
  previous: TranscriptEntry,
  incoming: TranscriptEntry
): boolean {
  if (previous.role !== incoming.role) {
    return false;
  }

  const previousText = normalizeTranscriptText(previous.text);
  const incomingText = normalizeTranscriptText(incoming.text);

  if (previousText.length < 12 || incomingText.length < 12) {
    return false;
  }

  const hasPrefixRelation =
    incomingText.startsWith(previousText) || previousText.startsWith(incomingText);

  if (!hasPrefixRelation) {
    return false;
  }

  return isCloseInTime(previous.timestamp, incoming.timestamp, 10000);
}

function mergeTranscriptEntries(
  previous: TranscriptEntry[],
  incoming: TranscriptEntry
): TranscriptEntry[] {
  const lastEntry = previous[previous.length - 1];
  if (!lastEntry) {
    return [incoming];
  }

  const replaceLast = () => [
    ...previous.slice(0, -1),
    {
      ...incoming,
      id: lastEntry.id,
    },
  ];

  if (
    incoming.transcriptType === 'partial' &&
    lastEntry.transcriptType === 'partial' &&
    lastEntry.role === incoming.role
  ) {
    return replaceLast();
  }

  if (
    incoming.transcriptType === 'final' &&
    lastEntry.transcriptType === 'partial' &&
    lastEntry.role === incoming.role
  ) {
    return replaceLast();
  }

  if (
    incoming.transcriptType === 'final' &&
    lastEntry.transcriptType === 'final' &&
    lastEntry.role === incoming.role &&
    normalizeTranscriptText(lastEntry.text) === normalizeTranscriptText(incoming.text)
  ) {
    return replaceLast();
  }

  if (isLikelyIncrementalTranscript(lastEntry, incoming)) {
    return replaceLast();
  }

  return [...previous, incoming];
}

function getPublicKey(): string {
  const publicKey = import.meta.env.VITE_VAPI_PUBLIC_KEY;
  if (!publicKey) {
    throw new Error('Missing VITE_VAPI_PUBLIC_KEY');
  }
  return publicKey;
}

function toMicrophonePermission(state: string | undefined): MicrophonePermission {
  if (state === 'granted' || state === 'prompt' || state === 'denied') {
    return state;
  }
  return 'unsupported';
}

async function queryMicrophonePermission(): Promise<MicrophonePermission> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.permissions ||
    typeof navigator.permissions.query !== 'function'
  ) {
    return 'unsupported';
  }

  try {
    const result = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return toMicrophonePermission(result.state);
  } catch {
    return 'unsupported';
  }
}

export function useVapi(): UseVapiReturn {
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [events, setEvents] = useState<VapiEvent[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isMuted, setIsMutedState] = useState(false);
  const [micPermission, setMicPermission] =
    useState<MicrophonePermission>('unsupported');

  const vapiRef = useRef<VapiInstanceLike | null>(null);
  const connectResolveRef = useRef<(() => void) | null>(null);
  const connectRejectRef = useRef<((error: Error) => void) | null>(null);
  const connectTimeoutRef = useRef<number | null>(null);

  const pushEvent = useCallback((type: string, payload?: unknown) => {
    const event: VapiEvent = {
      id: createId('event'),
      type,
      timestamp: new Date().toISOString(),
      payload,
    };

    setEvents((previous) => {
      const next = [...previous, event];
      return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
    });
  }, []);

  const clearConnectWatchdog = useCallback(() => {
    if (connectTimeoutRef.current !== null) {
      window.clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  }, []);

  const resolvePendingConnect = useCallback(() => {
    clearConnectWatchdog();
    if (connectResolveRef.current) {
      connectResolveRef.current();
    }
    connectResolveRef.current = null;
    connectRejectRef.current = null;
  }, [clearConnectWatchdog]);

  const rejectPendingConnect = useCallback(
    (connectError: Error) => {
      clearConnectWatchdog();
      if (connectRejectRef.current) {
        connectRejectRef.current(connectError);
      }
      connectResolveRef.current = null;
      connectRejectRef.current = null;
    },
    [clearConnectWatchdog]
  );

  const refreshMicrophonePermission = useCallback(async () => {
    const next = await queryMicrophonePermission();
    setMicPermission(next);
    return next;
  }, []);

  const requestMicrophoneAccess = useCallback(async (): Promise<MicrophonePermission> => {
    const permission = await refreshMicrophonePermission();

    if (permission === 'denied') {
      const deniedError = new Error(
        'Microphone permission is blocked. Enable microphone access in browser settings and retry.'
      );
      setMicPermission('denied');
      pushEvent('microphone-denied', { message: deniedError.message });
      throw deniedError;
    }

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
      const unsupportedError = new Error(
        'Microphone access is not supported in this browser.'
      );
      setMicPermission('unsupported');
      pushEvent('microphone-unsupported', { message: unsupportedError.message });
      throw unsupportedError;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMicPermission('granted');
      pushEvent('microphone-access-granted');
      return 'granted';
    } catch (micError) {
      const details = extractErrorParts(micError);
      const errorName = asString(asRecord(micError)?.name);
      let message = details.message ?? 'Microphone access failed.';

      if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
        message = 'Microphone access was denied. Allow access and retry.';
        setMicPermission('denied');
      }

      const normalized = new Error(message);
      pushEvent('microphone-access-error', {
        message,
        type: details.type,
        stage: details.stage,
        errorName,
      });
      throw normalized;
    }
  }, [pushEvent, refreshMicrophonePermission]);

  useEffect(() => {
    let mounted = true;
    let permissionStatus: PermissionStatus | null = null;

    const initializePermissionMonitoring = async () => {
      const permission = await queryMicrophonePermission();
      if (!mounted) {
        return;
      }

      setMicPermission(permission);
      pushEvent('microphone-permission', { state: permission });

      if (
        typeof navigator === 'undefined' ||
        !navigator.permissions ||
        typeof navigator.permissions.query !== 'function'
      ) {
        return;
      }

      try {
        const result = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        if (!mounted) {
          return;
        }

        permissionStatus = result;
        result.onchange = () => {
          const next = toMicrophonePermission(result.state);
          setMicPermission(next);
          pushEvent('microphone-permission-changed', { state: next });
        };
      } catch {
        // Ignore permission query failures in unsupported browsers.
      }
    };

    void initializePermissionMonitoring();

    return () => {
      mounted = false;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [pushEvent]);

  useEffect(() => {
    let mounted = true;
    let instance: VapiInstanceLike | null = null;
    const unsubscribers: Array<() => void> = [];

    try {
      instance = new Vapi(getPublicKey()) as unknown as VapiInstanceLike;
      vapiRef.current = instance;
      pushEvent('sdk-initialized');

      if (typeof instance.isMuted === 'function') {
        setIsMutedState(Boolean(instance.isMuted()));
      }
    } catch (initError) {
      const normalized = normalizeError(initError);
      setStatus('error');
      setError(normalized);
      pushEvent('sdk-initialization-error', { message: normalized.message });
      return () => {
        mounted = false;
      };
    }

    const bind = (eventName: string, handler: (payload?: unknown) => void) => {
      instance?.on(eventName, handler);

      if (instance?.off) {
        unsubscribers.push(() => {
          instance?.off?.(eventName, handler);
        });
        return;
      }

      if (instance?.removeListener) {
        unsubscribers.push(() => {
          instance?.removeListener?.(eventName, handler);
        });
      }
    };

    bind('call-start', (payload) => {
      if (!mounted) {
        return;
      }
      setStatus('connected');
      setError(null);
      pushEvent('call-start', payload);
      resolvePendingConnect();
    });

    bind('call-end', (payload) => {
      if (!mounted) {
        return;
      }
      setStatus('disconnected');
      pushEvent('call-end', payload);
      rejectPendingConnect(new Error('Call ended before connection completed.'));
    });

    bind('call-start-progress', (payload) => {
      if (!mounted) {
        return;
      }
      pushEvent('call-start-progress', payload);
    });

    bind('call-start-failed', (payload) => {
      if (!mounted) {
        return;
      }

      const details = extractErrorParts(payload);
      const normalized = normalizeError(payload);
      setStatus('error');
      setError(normalized);
      pushEvent('call-start-failed', {
        message: normalized.message,
        type: details.type,
        stage: details.stage,
        original: payload,
      });
      rejectPendingConnect(normalized);
    });

    bind('network-connection', (payload) => {
      if (!mounted) {
        return;
      }
      pushEvent('network-connection', payload);
    });

    bind('camera-error', (payload) => {
      if (!mounted) {
        return;
      }
      pushEvent('camera-error', payload);
    });

    bind('message', (payload) => {
      if (!mounted) {
        return;
      }

      pushEvent('message', payload);

      // Debug: Log all incoming messages to help troubleshoot transcript issues
      if (import.meta.env.DEV) {
        const p = payload as Record<string, unknown>;
        console.log('[VAPI message]', {
          type: p.type,
          role: p.role,
          hasTranscript: !!p.transcript,
          hasText: !!p.text,
          hasMessage: !!p.message,
          raw: payload,
        });
      }

      const entry = toTranscriptEntry(payload);
      if (!entry) {
        if (import.meta.env.DEV) {
          console.log('[VAPI message] Skipped - not a transcript entry');
        }
        return;
      }

      if (import.meta.env.DEV) {
        console.log('[VAPI transcript]', entry.role, entry.text.substring(0, 50));
      }

      setTranscript((previous) => {
        const next = mergeTranscriptEntries(previous, entry);
        return next.length > MAX_TRANSCRIPT_ENTRIES
          ? next.slice(next.length - MAX_TRANSCRIPT_ENTRIES)
          : next;
      });
    });

    bind('speech-start', (payload) => {
      if (!mounted) {
        return;
      }
      pushEvent('speech-start', payload);
    });

    bind('speech-end', (payload) => {
      if (!mounted) {
        return;
      }
      pushEvent('speech-end', payload);
    });

    bind('volume-level', (payload) => {
      if (!mounted) {
        return;
      }
      pushEvent('volume-level', payload);
    });

    bind('error', (payload) => {
      if (!mounted) {
        return;
      }

      const details = extractErrorParts(payload);
      const normalized = normalizeError(payload);
      setStatus('error');
      setError(normalized);
      pushEvent('error', {
        message: normalized.message,
        type: details.type,
        stage: details.stage,
        original: payload,
      });
      rejectPendingConnect(normalized);
    });

    return () => {
      mounted = false;
      clearConnectWatchdog();
      if (connectRejectRef.current) {
        connectRejectRef.current(
          new Error('VAPI client was reset before connection completed.')
        );
      }
      connectResolveRef.current = null;
      connectRejectRef.current = null;

      unsubscribers.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch {
          // Ignore listener cleanup errors.
        }
      });

      try {
        instance?.stop();
      } catch {
        // Ignore stop errors during unmount cleanup.
      }

      vapiRef.current = null;
    };
  }, [clearConnectWatchdog, pushEvent, rejectPendingConnect, resolvePendingConnect]);

  const startCall = useCallback(
    async (input: StartCallInput) => {
      const instance = vapiRef.current;

      if (!instance) {
        const missingClientError = new Error('VAPI SDK not initialized');
        setStatus('error');
        setError(missingClientError);
        pushEvent('start-call-error', { message: missingClientError.message });
        throw missingClientError;
      }

      if (connectRejectRef.current) {
        rejectPendingConnect(
          new Error('Previous connection attempt was interrupted by a new request.')
        );
      }

      await requestMicrophoneAccess();

      setStatus('connecting');
      setError(null);
      pushEvent('start-call-requested', {
        assistantId: input.assistantId,
        callId: input.call?.id ?? null,
      });

      const waitForCallStart = new Promise<void>((resolve, reject) => {
        connectResolveRef.current = resolve;
        connectRejectRef.current = reject;

        connectTimeoutRef.current = window.setTimeout(() => {
          const timeoutError = new Error(
            'Connection timed out after 20 seconds. Check microphone permissions and network, then retry.'
          );
          setStatus('error');
          setError(timeoutError);
          pushEvent('connect-timeout', {
            assistantId: input.assistantId,
            callId: input.call?.id ?? null,
            timeoutMs: CONNECT_TIMEOUT_MS,
          });
          rejectPendingConnect(timeoutError);
        }, CONNECT_TIMEOUT_MS);
      });

      try {
        if (input.call?.webCallUrl && typeof instance.reconnect === 'function') {
          pushEvent('start-call-transport', {
            mode: 'reconnect',
            callId: input.call.id ?? null,
          });
          await instance.reconnect(input.call as Record<string, unknown>);
        } else {
          pushEvent('start-call-transport', {
            mode: 'start',
            usedFallback: Boolean(input.call?.webCallUrl && !instance.reconnect),
            assistantId: input.assistantId,
          });
          await instance.start(input.assistantId);
        }

        await waitForCallStart;
      } catch (startError) {
        const normalized = normalizeError(startError);
        setStatus('error');
        setError(normalized);
        pushEvent('start-call-error', {
          assistantId: input.assistantId,
          callId: input.call?.id ?? null,
          message: normalized.message,
        });
        rejectPendingConnect(normalized);
        throw normalized;
      }
    },
    [pushEvent, rejectPendingConnect, requestMicrophoneAccess]
  );

  const endCall = useCallback(() => {
    const instance = vapiRef.current;
    if (!instance) {
      return;
    }

    pushEvent('end-call-requested');

    try {
      instance.stop();
      setStatus('disconnected');
      rejectPendingConnect(new Error('Call was manually ended before connection completed.'));
    } catch (stopError) {
      const normalized = normalizeError(stopError);
      setStatus('error');
      setError(normalized);
      pushEvent('end-call-error', { message: normalized.message });
    }
  }, [pushEvent, rejectPendingConnect]);

  const setMuted = useCallback(
    (muted: boolean) => {
      const instance = vapiRef.current;
      if (!instance || typeof instance.setMuted !== 'function') {
        const muteError = new Error('Mute controls are not available');
        setError(muteError);
        setStatus('error');
        pushEvent('set-muted-error', { message: muteError.message, muted });
        return;
      }

      try {
        instance.setMuted(muted);
        setIsMutedState(muted);
        pushEvent('mute-changed', { muted });
      } catch (muteUpdateError) {
        const normalized = normalizeError(muteUpdateError);
        setStatus('error');
        setError(normalized);
        pushEvent('set-muted-error', {
          muted,
          message: normalized.message,
        });
      }
    },
    [pushEvent]
  );

  return {
    status,
    transcript,
    events,
    error,
    micPermission,
    requestMicrophoneAccess,
    startCall,
    endCall,
    isMuted,
    setMuted,
  };
}
